import React from "react";
import { decrypt } from "../Core/utils/others";
import { StatsigFlagProvider, tierFor } from "./providers/statsig";

// The FE spelling of the cross-repo ENVIRONMENT convention: CRA only exposes
// REACT_APP_*-prefixed vars to the browser bundle.
export const environment = () =>
  process.env.REACT_APP_ENVIRONMENT || "local";

export const flagConfig = () => ({
  provider: process.env.REACT_APP_FEATURE_FLAG_PROVIDER,
  sdkKey: process.env.REACT_APP_STATSIG_CLIENT_KEY,
  environment: environment(),
});

// Same rule as Epicenter: the key decides. Key present → Statsig in every
// environment (the tier follows REACT_APP_ENVIRONMENT); key absent or the
// explicit REACT_APP_FEATURE_FLAG_PROVIDER=local override (e2e/CI) → offline
// local checker. An unrecognized environment also degrades to offline,
// mirroring Epicenter's throw-then-degrade.
export const statsigEnabled = (config) =>
  config.provider !== "local" && !!config.sdkKey && !!tierFor(config.environment);

// Core/utils/auth.js#saveSession fires this on every login, session patch,
// and profile switch — all of which are in-app SPA navigations (history.replace/push),
// never a reload. Without listening for it, the Statsig client keeps
// evaluating the pre-login identity (often "anonymous") until the next
// window focus or the 5-minute refresh interval. Logout is unaffected: it
// reloads the page (Core/sessionExpired.js), which remounts this provider
// with a fresh sessionUser() snapshot anyway.
const SESSION_REFRESH_EVENTS = ["sandbox_user_update"];

// Re-read per call — module-scope snapshots of the user blob are a documented incident class.
// Sandbox's session is written by Core/utils/auth.js#saveSession into the
// "sandbox_user" localStorage key (role, user_id/userId/studentId, userName).
// Logged out → anonymous (fixed, never random); student/parent sessions key
// off their id (percentage ramps bucket-hash the userID, so a stable id keeps
// one person on one side of a ramp); staff sessions (admin, mentor, csm, …)
// key off userName when available, falling back to their id.
export const sessionUser = () => {
  try {
    const user = decrypt(localStorage.getItem("sandbox_user"));
    if (!user) return { userID: "anonymous" };
    const id = user.studentId || user.userId || user.user_id;
    if (user.role === "student") {
      return {
        userID: id ? `student:${id}` : "anonymous",
        custom: { role: user.role, kind: "sso" },
      };
    }
    if (user.role === "parent") {
      return {
        userID: id ? `parent:${id}` : "anonymous",
        custom: { role: user.role, kind: "sso" },
      };
    }
    if (!user.userName && !id) return { userID: "anonymous" };
    return {
      userID: user.userName || String(id),
      custom: { role: user.role, kind: "staff", user_id: id },
    };
  } catch {
    return { userID: "anonymous" };
  }
};

export const FeatureFlagProvider = ({ children }) => {
  const config = flagConfig();
  if (statsigEnabled(config)) {
    return (
      <StatsigFlagProvider
        sdkKey={config.sdkKey}
        environment={config.environment}
        user={sessionUser}
        refreshOn={SESSION_REFRESH_EVENTS}
      >
        {children}
      </StatsigFlagProvider>
    );
  }
  // FeatureFlagContext's default value already serves the local checker.
  return <>{children}</>;
};

export { FeatureFlag } from "./activeFlags";
export {
  useFeatureFlag,
  useFeatureFlags,
  useFeatureFlagsReady,
} from "./context";
export { FLAGS } from "./registry";
